import UrlTableMenuFilterList from "components-for/tables/url-table-menu-filter-list";
import UrlTableMenuSortOptions from "components-for/tables/url-table-menu-sort-options";
import { decamelize } from "humps";
import { FunctionComponent, memo } from "react";
import { IInteraction, InteractionMetric } from "@edgetier/watchtower-types";
import { INTERACTIONS_TABLE_PREFIX } from "constants/interactions";
import { IProps } from "./interactions-table-metrics-column.types";
import { isOrderedByAscending } from "utilities/is-ordered-by-ascending";
import { isOrderedByProperty } from "utilities/is-ordered-by-property";
import { useInteractionMetricConversion } from "hooks-for/metrics/use-interaction-metric-conversion";
import { EXPERIENCE_SCORE_METRIC_ENABLED } from "constants/feature-flags";
import {
    BooleanIcon,
    Cell,
    Column,
    formatEnumOptions,
    Heading,
    NumericValue,
    TableMenuSection,
    TimePeriodValue,
    useQueryParameters,
} from "@edgetier/client-components";
import { useFilterAvailableMetrics } from "hooks-for/metrics/use-filter-available-metrics";
import "./interactions-table-metrics-column.scss";

/**
 * A column to display an interaction metric. The user can choose which metric to display in a menu.
 * @returns Table column.
 */
const InteractionsTableMetricsColumn: FunctionComponent<IProps> = ({
    interactionMetric: initialInteractionMetric = InteractionMetric.HandlingTime,
}) => {
    const { interactionMetric, orderBy } = useQueryParameters(
        { interactionMetric: (value) => value as InteractionMetric, orderBy: String },
        { defaults: { interactionMetric: initialInteractionMetric }, prefix: INTERACTIONS_TABLE_PREFIX }
    );

    const { metricLabel } = useInteractionMetricConversion(interactionMetric);

    const isTimeInteractionMetric =
        InteractionMetric.AverageResponseTime === interactionMetric ||
        InteractionMetric.FirstResponseTime === interactionMetric ||
        InteractionMetric.HandlingTime === interactionMetric ||
        InteractionMetric.QueueTime === interactionMetric;

    const interactionMetricExcludeOption = [
        ...(EXPERIENCE_SCORE_METRIC_ENABLED ? [] : [InteractionMetric.ExperienceScore]),
    ];
    const isBooleanInteractionMetric = InteractionMetric.QueryResolution === interactionMetric;
    const metricsAvailable = useFilterAvailableMetrics(interactionMetricExcludeOption);

    return (
        <Column>
            <Heading
                isSorted={isOrderedByProperty(decamelize(interactionMetric), orderBy)}
                isSortedAscending={isOrderedByAscending(decamelize(interactionMetric), orderBy)}
                menu={(closeMenu) => (
                    <>
                        <TableMenuSection>
                            <UrlTableMenuSortOptions
                                property={interactionMetric}
                                queryParameterPrefix={INTERACTIONS_TABLE_PREFIX}
                            />
                        </TableMenuSection>

                        <TableMenuSection>
                            <UrlTableMenuFilterList
                                labelProperty="label"
                                items={formatEnumOptions(metricsAvailable)}
                                queryParameter="interactionMetric"
                                queryParameterPrefix={INTERACTIONS_TABLE_PREFIX}
                                valueProperty="value"
                                onItemClick={closeMenu}
                            />
                        </TableMenuSection>
                    </>
                )}
            >
                {metricLabel}
            </Heading>

            <Cell<IInteraction>>
                {({ metrics }) => (
                    <div className="interactions-table-metrics-column">
                        {isTimeInteractionMetric ? (
                            <TimePeriodValue seconds={metrics[interactionMetric]} />
                        ) : isBooleanInteractionMetric ? (
                            <BooleanIcon value={metrics[interactionMetric]} />
                        ) : (
                            <NumericValue value={metrics[interactionMetric]} />
                        )}
                    </div>
                )}
            </Cell>
        </Column>
    );
};

export default memo(InteractionsTableMetricsColumn);
